import { clampPercent, formatPercent } from '@/lib/format'

export type ProgressTone = 'accent' | 'ok' | 'warn' | 'danger'

const TONES: Record<ProgressTone, string> = {
  accent: 'bg-accent',
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
}

/**
 * Transit progress: a squared track with a signal-amber fill, read as a slot
 * filling rather than a capsule inflating. The number is the load-bearing part
 * — it is set in mono with tabular figures so digits swap in place instead of
 * nudging the ETA beside them on every poll.
 */
export function ProgressBar({
  value,
  label,
  detail,
  tone = 'accent',
  indeterminate = false,
}: {
  value: number | null | undefined
  /** Names what is progressing, for screen readers and the row above the bar. */
  label: string
  detail?: string | null
  tone?: ProgressTone
  indeterminate?: boolean
}) {
  const percent = clampPercent(value)

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="signage truncate text-[9px] text-faint">{label}</span>
        <div className="flex shrink-0 items-baseline gap-2 font-mono text-[11px] tabular-nums">
          {detail ? <span className="text-faint">{detail}</span> : null}
          {/* Polite live region: progress updates every poll and must not steal focus. */}
          <span aria-live="polite" className="text-ink">
            {indeterminate ? '––' : formatPercent(percent)}
          </span>
        </div>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : Math.round(percent)}
        aria-valuetext={indeterminate ? 'Waiting for progress' : `${Math.round(percent)} percent`}
        className="h-1.5 w-full overflow-hidden rounded-[2px] bg-line"
      >
        {/* Indeterminate: a short stub that breathes, because the queue has an
            item but no estimate yet — not a bar sliding end to end, which would
            imply movement that is not happening. */}
        <div
          className={`h-full transition-[width] duration-700 ease-out ${TONES[tone]} ${
            indeterminate ? 'w-1/4 animate-shimmer' : ''
          }`}
          style={indeterminate ? undefined : { width: `${percent}%` }}
        />
      </div>
    </div>
  )
}
